import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { MerchantCategoryRestrictionsState } from 'src/feature/UpdatedPhysicalCardSettings/UpdatedPhysicalCardSettings'
import { useTranslate } from 'src/localization/Trns'
import type {
  MccGroupCatalogue,
  MccGroupRangeResponse,
} from 'src/models/mccGroup.models'
import type { CardManagementMccRestrictionMode } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsSegmented from '../SettingsSegmented/SettingsSegmented'
import style, { flyoutStyle } from './SpendRestrictionsTile.style'

const PILL_CAP = 4

type MerchantCategoryRestrictionsProps = {
  state: MerchantCategoryRestrictionsState
}

type Draft = {
  mode: CardManagementMccRestrictionMode
  exceptionMccGroupIds: string[]
  expectedVersion: number
}

const rangeKey = ({ lowest, highest }: MccGroupRangeResponse): string =>
  `${lowest}-${highest}`

const rangeLabel = ({ lowest, highest }: MccGroupRangeResponse): string =>
  lowest === highest ? lowest : `${lowest}\u2013${highest}`

function cappedPills(pills: ReactNode[]): ReactNode[] {
  if (pills.length <= PILL_CAP) {
    return pills
  }
  return [
    ...pills.slice(0, PILL_CAP),
    <Pill key="more" size="small">{`+${pills.length - PILL_CAP} more`}</Pill>,
  ]
}

function sameSelection(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    [...left].sort().every((id, index) => id === [...right].sort()[index])
  )
}

function MccCodeExpander(props: {
  category: MccGroupCatalogue
}): ReactElement | null {
  const { category } = props
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const listId = useId()
  const ranges = category.validMccRanges

  if (ranges.length === 0) {
    return null
  }

  const count = String(ranges.length)
  const rangeCount = ranges.filter(
    ({ lowest, highest }) => lowest !== highest,
  ).length
  let label: string
  if (rangeCount === 0) {
    label =
      ranges.length === 1
        ? translate('1 code')
        : translate('${count} codes', { count })
  } else if (rangeCount === ranges.length) {
    label =
      ranges.length === 1
        ? translate('1 range')
        : translate('${count} ranges', { count })
  } else {
    label = translate('${count} codes & ranges', { count })
  }

  return (
    <div className="SpendRestrictionsTile-codes">
      <button
        type="button"
        className="SpendRestrictionsTile-codesToggle"
        aria-expanded={open}
        aria-label={translate('Merchant category codes for ${name}: ${label}', {
          name: category.name,
          label,
        })}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
      </button>
      {open && (
        <ul
          id={listId}
          className="SpendRestrictionsTile-codeList"
          aria-label={translate('Merchant category codes for ${name}', {
            name: category.name,
          })}
        >
          {ranges.map((range) => (
            <li key={rangeKey(range)} className="SpendRestrictionsTile-codeRow">
              <span className="SpendRestrictionsTile-code">
                {rangeLabel(range)}
              </span>
              {range.description && (
                <span className="SpendRestrictionsTile-codeLabel">
                  {range.description}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function MerchantCategoriesDrawer(props: {
  open: boolean
  state: MerchantCategoryRestrictionsState
  draft: Draft
  isDirty: boolean
  categoryById: Map<string, MccGroupCatalogue>
  onClose: () => void
  onDraft: (draft: Draft) => void
  onSave: () => void
  onReload: () => void
}): ReactElement {
  const {
    open,
    state,
    draft,
    isDirty,
    categoryById,
    onClose,
    onDraft,
    onSave,
    onReload,
  } = props
  const translate = useTranslate()
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!open) {
      setQuery('')
    }
  }, [open])

  const listLabel =
    draft.mode === 'ALLOW_ALL_EXCEPT'
      ? translate('Blocked')
      : translate('Allowed')
  const addVerb =
    draft.mode === 'ALLOW_ALL_EXCEPT' ? translate('block') : translate('allow')
  const requirements = state.manageRequirements
  const selectionLimitReached =
    requirements !== undefined &&
    draft.exceptionMccGroupIds.length >= requirements.maxExceptionMccGroupIds
  const addable = state.catalogue.filter(
    (category) =>
      !selectionLimitReached &&
      !draft.exceptionMccGroupIds.includes(category.id) &&
      !requirements?.excludedMccGroupIds.includes(category.id) &&
      category.name.toLowerCase().includes(query.trim().toLowerCase()),
  )
  const modeOptions = [
    {
      label: translate('Allow all except'),
      value: 'ALLOW_ALL_EXCEPT' as const,
    },
    {
      label: translate('Block all except'),
      value: 'BLOCK_ALL_EXCEPT' as const,
    },
  ].filter(
    ({ value }) =>
      requirements === undefined || requirements.supportedModes.includes(value),
  )
  const draftIsValid =
    requirements === undefined ||
    (requirements.supportedModes.includes(draft.mode) &&
      draft.exceptionMccGroupIds.length <=
        requirements.maxExceptionMccGroupIds &&
      draft.exceptionMccGroupIds.every(
        (groupId) => !requirements.excludedMccGroupIds.includes(groupId),
      ))
  const staleVersion = state.saveErrorCode === 'CARD-MGMT.19'
  const reloadRequired = state.unknownOutcome || staleVersion
  const updateExceptions = (exceptionMccGroupIds: string[]): void =>
    onDraft({ ...draft, exceptionMccGroupIds })

  return (
    <BaseFlyout
      className="SpendRestrictionsDrawer"
      css={flyoutStyle}
      isOpen={open}
      onClose={onClose}
      aria-label={translate('Merchant categories')}
    >
      <div className="SpendRestrictionsTile-drawer" css={style}>
        <div className="SpendRestrictionsTile-drawerHeading">
          <h2>{translate('Merchant categories')}</h2>
          <p className="SpendRestrictionsTile-drawerIntro">
            {translate(
              'Choose which merchant categories this card can use. Saved changes are enforced on the card.',
            )}
          </p>
          <p className="SpendRestrictionsTile-drawerLead">
            {translate(
              'Each category groups a set of Mastercard merchant category codes (MCCs).',
            )}
          </p>
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Mode')}
          </div>
          <SettingsSegmented
            ariaLabel={translate('Merchant-category default')}
            value={draft.mode}
            options={modeOptions}
            onChange={(mode) =>
              onDraft({
                ...draft,
                mode: mode as CardManagementMccRestrictionMode,
              })
            }
          />
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {translate('Add a category to ${verb}', { verb: addVerb })}
          </div>
          <ExtUITextInput
            variant="small"
            label={translate('Search categories')}
            placeholder={translate('Type a category name…')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query.trim() !== '' && (
            <ul className="SpendRestrictionsTile-addList">
              {selectionLimitReached ? (
                <li className="SpendRestrictionsTile-addEmpty" role="status">
                  {translate(
                    'You can select up to ${count} merchant categories.',
                    {
                      count: String(requirements?.maxExceptionMccGroupIds),
                    },
                  )}
                </li>
              ) : addable.length === 0 ? (
                <li className="SpendRestrictionsTile-addEmpty">
                  {translate('No matches')}
                </li>
              ) : (
                addable.map((category) => (
                  <li
                    key={category.id}
                    className="SpendRestrictionsTile-addItem"
                  >
                    <button
                      type="button"
                      className="SpendRestrictionsTile-addRow"
                      onClick={() => {
                        updateExceptions([
                          ...draft.exceptionMccGroupIds,
                          category.id,
                        ])
                        setQuery('')
                      }}
                    >
                      <span>{category.name}</span>
                      <span aria-hidden="true">{`+ ${addVerb}`}</span>
                    </button>
                    <div className="SpendRestrictionsTile-addCodes">
                      <MccCodeExpander category={category} />
                    </div>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker forList">
            {draft.exceptionMccGroupIds.length > 0
              ? `${listLabel} · ${draft.exceptionMccGroupIds.length}`
              : listLabel}
          </div>
          {draft.exceptionMccGroupIds.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {draft.mode === 'ALLOW_ALL_EXCEPT'
                ? translate('No exceptions — every category is allowed.')
                : translate('No exceptions — every category is blocked.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList">
              {draft.exceptionMccGroupIds.map((groupId) => {
                const category = categoryById.get(groupId)
                const name = category?.name ?? groupId
                return (
                  <li
                    key={groupId}
                    className="SpendRestrictionsTile-exceptionRow"
                  >
                    <div className="SpendRestrictionsTile-exceptionMain">
                      <span className="SpendRestrictionsTile-exceptionName">
                        {name}
                      </span>
                      {category && <MccCodeExpander category={category} />}
                    </div>
                    <span className="SpendRestrictionsTile-exceptionControl">
                      <Pill
                        size="small"
                        color={
                          draft.mode === 'ALLOW_ALL_EXCEPT'
                            ? 'severe'
                            : 'highlight'
                        }
                      >
                        {listLabel}
                      </Pill>
                      <button
                        type="button"
                        className="SpendRestrictionsTile-removeButton"
                        aria-label={translate('Remove ${name}', { name })}
                        onClick={() =>
                          updateExceptions(
                            draft.exceptionMccGroupIds.filter(
                              (candidate) => candidate !== groupId,
                            ),
                          )
                        }
                      >
                        {'×'}
                      </button>
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {reloadRequired ? (
          <div className="SpendRestrictionsTile-drawerFooter" role="alert">
            <p>
              {staleVersion
                ? translate(
                    'Merchant category restrictions changed. Reload the latest restrictions before editing again.',
                  )
                : translate(
                    'The save outcome is unknown. Reload the latest restrictions before editing again.',
                  )}
            </p>
            <Button variant="secondary" dropShadow={false} onClick={onReload}>
              {translate('Reload')}
            </Button>
          </div>
        ) : state.saveError ? (
          <div className="SpendRestrictionsTile-drawerFooter" role="alert">
            <p>
              {translate(
                'We could not save merchant category restrictions. Try again.',
              )}
            </p>
          </div>
        ) : null}

        <div className="SpendRestrictionsTile-drawerActions">
          <Button variant="secondary" dropShadow={false} onClick={onClose}>
            {translate('Cancel')}
          </Button>
          <Button
            variant="primary"
            dropShadow={false}
            disabled={
              !isDirty || state.isSaving || reloadRequired || !draftIsValid
            }
            aria-busy={state.isSaving}
            onClick={onSave}
            data-testid="save-merchant-categories"
          >
            {state.isSaving ? translate('Saving…') : translate('Save')}
          </Button>
        </div>
      </div>
    </BaseFlyout>
  )
}

export default function MerchantCategoryRestrictions(
  props: MerchantCategoryRestrictionsProps,
): ReactElement | null {
  const { state } = props
  const translate = useTranslate()
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>()
  const [awaitingVersion, setAwaitingVersion] = useState<number>()
  const categoryById = useMemo(
    () => new Map(state.catalogue.map((category) => [category.id, category])),
    [state.catalogue],
  )

  const focusTrigger = (): void => {
    editButtonRef.current?.focus()
  }
  const closeDrawer = (): void => {
    setDrawerOpen(false)
    setDraft(undefined)
    setAwaitingVersion(undefined)
    focusTrigger()
  }
  const reloadDraft = (): void => {
    void state
      .onReload()
      .then(closeDrawer)
      .catch(() => undefined)
  }

  useEffect(() => {
    if (
      awaitingVersion !== undefined &&
      state.restrictions !== undefined &&
      state.restrictions.version !== awaitingVersion
    ) {
      setAwaitingVersion(undefined)
      setDraft(undefined)
      setDrawerOpen(false)
      focusTrigger()
    }
  }, [awaitingVersion, state.restrictions])

  if (!state.canView) {
    return null
  }

  if (state.isLoading) {
    return (
      <SettingsDisclosure
        title={translate('Merchant categories')}
        summary={translate('Loading…')}
        actionLabel={translate('View')}
      >
        <p
          className="SpendRestrictionsTile-body"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          {translate('Loading merchant category restrictions…')}
        </p>
      </SettingsDisclosure>
    )
  }

  if (state.loadError || !state.restrictions) {
    return (
      <SettingsDisclosure
        title={translate('Merchant categories')}
        summary={translate('Unavailable')}
        actionLabel={translate('View')}
      >
        <p className="SpendRestrictionsTile-body" role="alert">
          {translate('Merchant category restrictions are unavailable.')}
        </p>
        <Button
          variant="secondary"
          dropShadow={false}
          onClick={() => void state.onReload().catch(() => undefined)}
        >
          {translate('Reload')}
        </Button>
      </SettingsDisclosure>
    )
  }

  const { restrictions } = state
  const nameForGroup = (groupId: string): string =>
    categoryById.get(groupId)?.name ?? groupId
  const savedNames = restrictions.exceptionMccGroupIds.map(nameForGroup)
  const summary =
    savedNames.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {restrictions.mode === 'ALLOW_ALL_EXCEPT'
          ? translate('All categories allowed')
          : translate('All categories blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        <span className="SpendRestrictionsTile-summaryText">
          {restrictions.mode === 'ALLOW_ALL_EXCEPT'
            ? translate('Blocked:')
            : translate('Allowed only:')}
        </span>
        {cappedPills(
          savedNames.map((name, index) => (
            <Pill
              key={restrictions.exceptionMccGroupIds[index]}
              size="small"
              color={
                restrictions.mode === 'ALLOW_ALL_EXCEPT'
                  ? 'severe'
                  : 'highlight'
              }
            >
              {name}
            </Pill>
          )),
        )}
      </span>
    )
  let body: string
  if (savedNames.length === 0) {
    body =
      restrictions.mode === 'ALLOW_ALL_EXCEPT'
        ? translate('All categories allowed.')
        : translate('All categories blocked.')
  } else if (restrictions.mode === 'ALLOW_ALL_EXCEPT') {
    body = translate('Allows everywhere except ${list}.', {
      list: savedNames.join(', '),
    })
  } else {
    body = translate('Allows only ${list}.', { list: savedNames.join(', ') })
  }

  const openDrawer = (): void => {
    setDraft({
      mode: restrictions.mode,
      exceptionMccGroupIds: [...restrictions.exceptionMccGroupIds],
      expectedVersion: restrictions.version,
    })
    setDrawerOpen(true)
  }
  const isDirty =
    draft !== undefined &&
    (draft.mode !== restrictions.mode ||
      !sameSelection(
        draft.exceptionMccGroupIds,
        restrictions.exceptionMccGroupIds,
      ))
  const saveDraft = (): void => {
    if (!draft || !isDirty || state.isSaving || state.unknownOutcome) {
      return
    }
    setAwaitingVersion(restrictions.version)
    void state.onSave(draft).catch(() => setAwaitingVersion(undefined))
  }

  return (
    <>
      <SettingsDisclosure
        title={translate('Merchant categories')}
        summary={summary}
        actionLabel={state.canManage ? translate('Edit') : translate('View')}
      >
        <p className="SpendRestrictionsTile-body">{body}</p>
        {state.canManage ? (
          <Button
            ref={editButtonRef}
            variant="secondary"
            dropShadow={false}
            onClick={openDrawer}
            data-testid="edit-merchant-categories"
          >
            {translate('Edit categories')}
          </Button>
        ) : (
          <p
            className="SpendRestrictionsTile-body"
            data-testid="merchant-categories-view-only"
          >
            {state.manageReasonCode === 'ROLE_NOT_ALLOWED'
              ? translate(
                  'Only an Admin or Owner can change merchant categories.',
                )
              : translate(
                  'Merchant category changes are not available for this card.',
                )}
          </p>
        )}
      </SettingsDisclosure>
      {draft && (
        <MerchantCategoriesDrawer
          open={drawerOpen}
          state={state}
          draft={draft}
          isDirty={isDirty}
          categoryById={categoryById}
          onClose={closeDrawer}
          onDraft={setDraft}
          onSave={saveDraft}
          onReload={reloadDraft}
        />
      )}
    </>
  )
}
